import React, { memo } from 'react'
import { Link } from 'react-router-dom'

import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import CancelIcon from '@mui/icons-material/Cancel';
import SvgIcon from '@mui/material/SvgIcon';

import {ReactComponent as WaitIcon} from '../styles/Timer3.svg';

import clsx from 'clsx';
import useStyles from '../styles/Styles'

import _ from 'lodash';
import Pluralize from 'pluralize';

const CoursesHelp = memo(({helpOpen, handleChange, isSm, 
	coursesCapLabel, courseCapLabel, coursesLabel, courseLabel,
	category, member_no, general_enrol_on, early_enrol_on, userCanEnrol
	}) => {
		
	const classes = useStyles();
	
	const getYouCanEnrol = () => {
		if(member_no === 0){
			if(general_enrol_on){
				return (<Typography paragraph>Only logged-in members can enrol in {coursesLabel}. Please <Link to='/login'>log in</Link>.</Typography>);
			} else if(early_enrol_on){
				return <Typography paragraph>Early enrolments are currently on: If you are a member with early enrolment permission, please <Link to='/login'>log in</Link>. If you don&apos;t have early enrolment permission, you will need to wait until general enrolments are open.</Typography>
			} else {
				return <Typography paragraph>Enrolments are currently off. Please check back later, or <Link to='/contact'>contact us</Link> for more information.</Typography>
			} 
		} else if(userCanEnrol){
			return <>
			<Typography gutterBottom>You are logged in and eligible to enrol in {coursesLabel}.</Typography>
			<Typography paragraph>
			Note that even though you are eligible to enrol, some {coursesLabel} may be unavailable for enrolment. For example, the {courseLabel} may be full or finished, or you may have already reached the maximum number of allowed {courseLabel} enrolments in that {_.toLower(category)}. 
			</Typography>
			</>
		} else if (early_enrol_on){
			return <Typography paragraph>Early enrolments are currently on, but you don&apos;t have early enrolment permission. You will be able to enrol once general enrolments are open.</Typography>
		} else {
			return <Typography paragraph>Enrolments are currently off, or you are not eligible to enrol. Please <Link to='/contact'>contact us</Link> for more information, if required.</Typography> 
		}
		
	}
	
	return (
			<Dialog
			open={helpOpen}
			onClose={() => handleChange(false)}
			fullScreen={!isSm}
			aria-labelledby="help-dialog-title"
			className={clsx(classes.settingsDialog, classes.otherContent)}
			>
			<DialogTitle id="help-dialog-title">{coursesCapLabel} Help</DialogTitle>
			<DialogContent dividers>
			<Typography variant='h5' component='h2' gutterBottom>
            Finding {coursesLabel}
			</Typography>
          <Typography gutterBottom>
          Scroll through the list to find {coursesLabel} that are of interest to you. You can click on the name of any {courseLabel} to view details.
          </Typography>
          <Typography gutterBottom>
          {coursesCapLabel} can be filtered and sorted by clicking on the FILTER button. For example, you might like to sort by day of week or {_.toLower(category)}, only view selected {_.toLower(Pluralize(category))}, or hide {coursesLabel} that are full or finished. You can also choose to view a different timespan, such as "This week" or "Next month".
          </Typography>
		  <Typography gutterBottom>When you are logged in, any {coursesLabel} in which you are currently enrolled or waitlisted will be marked with a blue letter E or W respectively:</Typography>
		  <Grid container spacing={1}>
		  <Grid item><Chip className={classes.chipE} label='Enrolled'
			avatar={<Avatar className={classes.avatarE}>E</Avatar>}
			/></Grid>
		  <Grid item><Chip className={classes.chipW} label='Waitlisted'
			avatar={<Avatar className={classes.avatarW}>W</Avatar>}
			/></Grid>
		  </Grid>
		  <Typography gutterBottom className={classes.gutterTop}>Any {courseLabel} that is currently in your cart will be marked with a yellow letter C:</Typography>
		  <Grid container spacing={1}>
		  <Grid item><Chip className={classes.chipC} label='In cart'
			avatar={<Avatar className={classes.avatarC}>C</Avatar>}
			/></Grid>
		  </Grid>
		  <Typography gutterBottom className={classes.gutterTop}>Any {courseLabel} from which your enrolment has been deleted will be marked with a grey letter D:</Typography>
		  <Grid container spacing={1}>
		  <Grid item><Chip className={classes.chipD} label='Deleted'
			avatar={<Avatar className={classes.avatarD}>D</Avatar>}
			/></Grid>
		  </Grid>
		  <Typography gutterBottom className={classes.gutterTop}>Some {coursesLabel} may have an icon denoting {courseLabel} status:<br/>
		  Grey lock icon: Closed to new enrolments.<br/>
		  Grey waiting icon: All enrolments waitlisted, zero maximum enrolments*.<br/>
		  Grey crossed icon: Full and only accepting waitlist enrolments.<br/>
		  Blue start icon: New (recently added to the timetable).</Typography>
		  <Grid container spacing={1}>
		  <Grid item><Chip className={classes.chipClosed}
				label='Closed'
				icon={<LockIcon/>}
			/></Grid>
		  <Grid item><Chip className={classes.chipClosed}
				label='All Wait'
				icon={<SvgIcon component={WaitIcon} />}
			/></Grid>
		  <Grid item><Chip component='span' className={classes.chipClosed}
				label='Full'
				icon={<CancelIcon/>}
			/></Grid>
		  <Grid item><Chip component='span' className={classes.chipNew}
				label='New'
				icon={<StarIcon/>}
			/></Grid>
		  </Grid>
		  <Typography paragraph variant={'body2'} className={classes.gutterTop}>*Usually, an "All Wait" {courseLabel} means that all enrolments are initially placed on a waitlist, and admission is up to the tutor/leader. Examples include {coursesLabel} such as advanced languages that require a minimum level of skill and thus are not suitable for beginners, or day trips that will only proceed if there are enough participants.</Typography>
		  
		  <Typography variant='h5' component='h2' gutterBottom>
            Enrolling in {coursesLabel}
			</Typography>
			<Typography gutterBottom>
			{courseCapLabel} selection works similar to shopping carts that you have probably used on other websites:</Typography>
			<ol className={classes.condensedList}>
			{member_no === 0 ? <li><Typography gutterBottom><Link to='/login'>Log in</Link> to U-MAS.</Typography></li>: null}
			<li><Typography gutterBottom>Add your preferred {coursesLabel} to your cart: An ADD TO CART button will be displayed at the bottom of the detail section of any available {courseLabel}. (Click on the {courseLabel} in the list to view the detail section.)</Typography></li>
			<li><Typography gutterBottom>When you have finished selecting {coursesLabel}, check out in order to confirm your selections and complete your enrolment.</Typography>
			<Typography paragraph><strong>Enrolments are not confirmed until you have checked out.</strong></Typography></li>
			</ol>
			<Typography variant='h5' component='h2' gutterBottom>
            Viewing your cart and checking out
			</Typography>
			<Typography paragraph>When there are items in your cart, the cart icon will be displayed at the {isSm ? 'top' : 'bottom'} of the screen: Click this icon to view the contents of your cart, remove {coursesLabel} from the cart, or proceed to checkout.
			</Typography>
			<Typography variant='h5' component='h2' gutterBottom>
			Can I enrol now?
			</Typography>
			{getYouCanEnrol()}
			</DialogContent>
			<DialogActions>
          <Button autoFocus onClick={() => handleChange(false)} color="primary">
            Close
          </Button>
        </DialogActions>
		</Dialog>
)})

export default CoursesHelp;